<template>
  <div class="flex flex-col sm:px-6 lg:px-8">
    <div class="sm:mx-auto sm:w-full sm:max-w-[520px]">
      <div class="items-center bg-white px-6 md:px-12 pb-6 shadow sm:rounded-lg">
        <SectionHeader
          class="mx-auto !justify-center pb-8 pt-14"
          :title="__('Sign In')"
        >
        </SectionHeader>
        <div>
          <div>
            <div class="pb-8">
              <div
                  class="mx-auto flex w-full justify-center gap-2 rounded-lg bg-white"
              >
                  <button
                      class="w-full selected bg-[#F6F3F9] flex-1 rounded-md px-8 py-3 font-medium leading-normal text-zinc-800 outline-none transition-colors hover:bg-secondary/5`"
                      @click="redirectTo('candidateLogin')"
                  >
                    {{ __('Candidate') }}
                  </button>
                  <button
                      class="w-full bg-[#FAFAFA] flex-1 rounded-md px-8 py-3 font-medium leading-normal text-zinc-800 outline-none transition-colors hover:bg-secondary/5"
                      @click="redirectTo('companyLogin')"
                  >
                    {{ __('Company') }}
                  </button>
              </div>
            </div>
            <div>
              <div class="space-y-12">
                <form @submit.prevent="handleSubmit">
                  <div class="flex flex-col">
                    <InputEmail
                      id="password_current"
                      v-model:value="values.login"
                      v-model:errors="errors.login"
                      :label="__('Email')"
                      :placeholder="__('Your email')"
                      class="pb-4"
                      required
                    />

                    <InputPassword
                      id="password"
                      v-model:value="values.password"
                      v-model:errors="errors.password"
                      :label="__('Password')"
                      :placeholder="__('Type your password')"
                      class="pb-8"
                      required
                    />
                    <div class="flex flex-wrap items-center justify-between gap-2 mb-8">
                      <InputCheckbox
                        id="remember"
                        v-model:value="values.remember"
                        v-model:errors="errors.remember"
                        :label="__('Remember me')"
                      />

                      <a @click="redirectTo('forgot')" class="text-md font-medium cursor-pointer text-primary underline whitespace-nowrap">
                        {{ __('Forgot your password?') }}
                      </a>
                    </div>
                  <div class="text-center">
                    <p class="text-sm">
                      {{ __("Don't have an account?") }}
                      <a @click="redirectTo('register')" class="font-medium cursor-pointer text-primary">{{__('Register now')}}</a>
                    </p>
                  </div>
                    <div
                      v-if="errorMessage || successMessage"
                      class="mt-8 flex flex-col"
                    >
                      <div
                        v-if="errorMessage"
                        class="flex items-center gap-1 rounded-lg border border-red-400 bg-red-100 p-4 text-red-700"
                      >
                        <span
                          class="material-symbols-outlined text-xl text-red-600"
                          style="font-variation-settings: 'FILL' 1"
                        >
                          error
                        </span>
                        <span>{{ errorMessage }}</span>
                        <button
                            type="button"
                          v-if="resendFromError"
                          @click="handleResendEmailClick"
                          class="font-medium underline cursor-pointer text-primary"
                      >
                        {{ __('Resend Email') }}
                      </button>
                      </div>

                      <div
                        v-if="successMessage"
                        class="flex items-center gap-2 rounded-lg border border-green-400 bg-green-100 p-4 text-green-700"
                      >
                        <span
                          class="material-symbols-outlined text-xl text-green-600"
                          style="font-variation-settings: 'FILL' 1"
                        >
                          check_circle
                        </span>
                        <span>{{ successMessage }}</span>
                      </div>
                    </div>
                  </div>

                  <div
                    class="mt-4 flex flex-col gap-2 md:flex-row-reverse md:justify-center items-center"
                  >
                    <Button
                      v-if="showResendButton"
                      @click="resendVerificationEmail"
                      class="rounded-lg   px-4 py-2 font-medium transition "
                      variant="outline"
                    >
                      {{ __('Resend Verification Email')}}
                    </Button>
                    <Button
                      v-else
                      class="flex items-center justify-center gap-1.5"
                      :type="isLoading ? 'button' : 'submit'"
                      :disabled="isLoading"
                      style="width: 10rem !important"
                    >
                      <Icon
                        v-if="isLoading"
                        class="animate-spin text-lg"
                        name="progress_activity"
                      />
                      <span>{{ __('Sign In') }}</span>
                    </Button>
                  </div>
                  <div>
                    <SocialLoginButton></SocialLoginButton>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import {ref, watch, onMounted, nextTick} from 'vue';
  import { __ } from '@/helpers';
  import { Button } from '../../../../components/block/button';
  import { Icon } from '../../../../components/block/icon';
  import { InputPassword } from '../../../../components/form/input-password';
  import { InputEmail } from '../../../../components/form/input-email';
  import { SectionHeader } from '../../../components/section-header';
  import { InputCheckbox } from '../../../../components/form/input-checkbox';
  import axios from 'axios';
  import SocialLoginButton from '../components/SocialLoginButton.vue';
  import {useToast} from "vue-toastification";


  const props = defineProps({
    successMessage: {
      type: String,
      default: null
    },
    errorMessage: {
      type: String,
      default: null
    },
    flash: String
  });

  const toast = useToast();

  const message = ref(props.flash);

  const redirectParam = ref(null);
  const actionParam   = ref(null);

  const values = ref({
    login: '',
    password: '',
    remember: false
  });

  const routes = {
    companyLogin: 'front.auth.company.login',
    candidateLogin: 'login.user',
    register: 'front.auth.register',
    forgot: 'front.auth.reset-password'
  }

  const redirectTo = (routeName) => {
    window.location.href = route(routes[routeName]);
  }

  const errors = ref({
    login: [],
    password: [],
    remember: []
  });

  const isLoading = ref(false);

  const errorMessage = ref('');
  const successMessage = ref('');
  const showResendButton = ref(false);
  const resendFromError = ref(false);

  const handleResendEmailClick = () => {
    resendVerificationEmail();
  }
  const resendVerificationEmail = async () => {
    if (!values.value.login) {
      errorMessage.value = 'Email is required.';
      successMessage.value = '';
      return;
    }

    errorMessage.value = '';
    successMessage.value = '';

    try {
      const response = await axios.post(route('resend-verification-token'), {
        email: values.value.login,
      });
      errorMessage.value = '';
      successMessage.value = response.data.message;
    } catch (error) {
      errorMessage.value = error.response?.data?.message || 'Something went wrong.';
      successMessage.value = '';
    }
  };


  async function handleSubmit() {
    const requiredFieldKeys = ['login', 'password'];
    let firstElement;

    requiredFieldKeys.forEach((key) => {
      if (!values.value[key]) {
        errors.value[key] = [__('This field is required')];

        const element = document.getElementById(key);

        if (element && !firstElement) {
          firstElement = element;
        }
      }
    });

    if (Object.values(errors.value).some((error) => error.length)) {
      if (firstElement) {
        firstElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    isLoading.value = true;
    errorMessage.value = '';
    showResendButton.value = false;
    resendFromError.value = false;

    try {
      if (redirectParam.value) values.value.redirect = redirectParam.value;
      if (actionParam.value)   values.value.action   = actionParam.value;

      const response = await axios.post('/login', values.value);

      if (response.data.status === 'success' || response.data.status === '2fa_required') {
        window.location.href = response.data.redirect;
      }
    } catch (error) {
      if (error?.response?.status === 403 && error.response.data.resend) {
        errorMessage.value = error.response.data.message;
        showResendButton.value = true;
        resendFromError.value = false;
      } else if (error?.response?.data?.message && error.response?.data?.resend_email) {
        errorMessage.value = error.response.data.message;
        resendFromError.value = true;
      } else if (error?.response?.data?.message) {
        errorMessage.value = error.response.data.message;
        resendFromError.value = false;
      } else {
        errorMessage.value = __(
          'An unexpected error has occurred. Please try again later.'
        );
        resendFromError.value = false;
      }
    }

    isLoading.value = false;
  }

  onMounted(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const redirect = params.get('redirect');
      const action = params.get('action');
      if (redirect) {
        redirectParam.value = redirect;
        actionParam.value = action;
      } else {
        const pending = sessionStorage.getItem('pending_challenge_start');
        if (pending) {
          redirectParam.value = `/challenges/${pending}`;
          actionParam.value = 'start';
        }
      }
    } catch (e) {
    }
    setTimeout(() => {
      if (message.value) {
        nextTick(() => {
          toast.success(message.value, {
            timeout: 7000,
          });

          setTimeout(() => {
            axios.delete(route("clear-reset-password-message")).catch(() => {});
            message.value = "";
          }, 5000);
        });
      }
    }, 500);
  });
</script>
